// Settings-01b — Invite Team Member modal.
//
// Posts to ``/api/v1/team-members/`` (handled by
// ``inviteTeamMember`` in api.ts). The backend handles email
// dispatch via the worker queue — no raw token ever lands on the
// FE. Reuses the broker-scoped title dropdown w/ add-new from the
// edit modal so the invitation captures the same metadata shape
// that ``Membership`` will hold once accepted.

import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Input,
  Modal,
  Select,
  Toggle,
} from "@/design-system";
import { usePermissions } from "@/features/auth";

import { RoleCheckboxes } from "./RoleCheckboxes";
import type { BrokerProductRole, InviteTeamMemberInput } from "./types";


const ADD_NEW_TITLE_VALUE = "__add_new__";


export interface InviteTeamMemberModalProps {
  open: boolean;
  titleOptions: string[];
  onClose: () => void;
  onSubmit: (input: InviteTeamMemberInput) => Promise<void> | void;
  saving?: boolean;
  error?: string | null;
}


export function InviteTeamMemberModal({
  open,
  titleOptions,
  onClose,
  onSubmit,
  saving = false,
  error = null,
}: InviteTeamMemberModalProps) {
  const permissions = usePermissions();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [titleSelect, setTitleSelect] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [isProducer, setIsProducer] = useState(false);
  const [roles, setRoles] = useState<BrokerProductRole[]>(["member"]);

  // Reset whenever the modal re-opens so a previous draft doesn't
  // leak between invites.
  useEffect(() => {
    if (!open) return;
    setFirstName("");
    setLastName("");
    setEmail("");
    setTitleSelect("");
    setNewTitle("");
    setIsProducer(false);
    setRoles(["member"]);
  }, [open]);

  const sortedTitleOptions = useMemo(
    () => Array.from(new Set(titleOptions)).sort(
      (a, b) => a.localeCompare(b),
    ),
    [titleOptions],
  );

  const resolvedTitle = (
    titleSelect === ADD_NEW_TITLE_VALUE ? newTitle : titleSelect
  ).trim();

  const canSubmit =
    email.trim().length > 0 && roles.length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      title: resolvedTitle,
      is_producer: isProducer,
      roles,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Invite team member"
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={onClose}
            disabled={saving}
            data-testid="invite-team-member-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={!canSubmit}
            data-testid="invite-team-member-submit"
          >
            Send invitation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && (
          <div
            role="alert"
            data-testid="invite-team-member-error"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            value={firstName}
            data-testid="invite-team-member-first-name"
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last name"
            value={lastName}
            data-testid="invite-team-member-last-name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <Input
          label="Email"
          type="email"
          required
          value={email}
          data-testid="invite-team-member-email"
          onChange={(e) => setEmail(e.target.value)}
          helper="They'll receive a branded invitation with an accept link valid for 7 days."
        />

        <div className="flex flex-col gap-1">
          <label
            htmlFor="invite-team-member-title-select"
            className="text-sm font-medium text-text-primary"
          >
            Title
          </label>
          <Select
            id="invite-team-member-title-select"
            data-testid="invite-team-member-title-select"
            value={
              titleSelect === ADD_NEW_TITLE_VALUE
                ? ADD_NEW_TITLE_VALUE
                : sortedTitleOptions.includes(titleSelect)
                  ? titleSelect
                  : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === ADD_NEW_TITLE_VALUE) {
                setTitleSelect(ADD_NEW_TITLE_VALUE);
              } else {
                setTitleSelect(v);
                setNewTitle("");
              }
            }}
          >
            <option value="">— None —</option>
            {sortedTitleOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value={ADD_NEW_TITLE_VALUE}>+ Add new title…</option>
          </Select>
          {titleSelect === ADD_NEW_TITLE_VALUE && (
            <Input
              data-testid="invite-team-member-title-new"
              placeholder="e.g. Director of Client Services"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          )}
        </div>

        <RoleCheckboxes
          value={roles}
          onChange={setRoles}
          disableOwnerToggle={!permissions.is_owner}
          testIdPrefix="invite-team-member-role"
        />

        <Toggle
          checked={isProducer}
          onChange={setIsProducer}
          data-testid="invite-team-member-is-producer"
          label="Producer"
        />
      </div>
    </Modal>
  );
}
